// ---------------------------------------------------------------------------
// compliance-hub.content.ts — Translated content for ComplianceHub page
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';
import { fr, de, ja, zh } from './compliance-hub.content.extra';

export interface ComplianceHubRegion {
  region: string;
  items: [string, string, string, string];
}

export interface ComplianceHubContent {
  heroBadge: string;
  heroTitle: string;
  heroSubtitle: string;
  regions: Record<string, ComplianceHubRegion>;
  ctaTitle: string;
  ctaSubtitle: string;
}

const en: ComplianceHubContent = {
  heroBadge: '6 Regions Covered',
  heroTitle: 'Regional Compliance',
  heroSubtitle:
    'Automated tax calculation, guest registration, and data protection across 6 global regions. Stay compliant effortlessly with built-in regulatory frameworks tailored to each jurisdiction.',
  regions: {
    'eu-uk': {
      region: 'EU & United Kingdom',
      items: [
        'GDPR data protection & right to erasure',
        'Multi-rate VAT automation (0-27%)',
        'PSD2/SCA payment compliance',
        'ePrivacy cookie consent management',
      ],
    },
    'united-states': {
      region: 'United States',
      items: [
        'State-by-state tax calculation',
        'PCI DSS self-assessment',
        'ADA compliance reporting',
        'Local occupancy tax automation',
      ],
    },
    'uae-middle-east': {
      region: 'UAE & Middle East',
      items: [
        'Tourism Dirham fee tracking',
        '5% VAT invoicing with TRN',
        'Municipality fee automation',
        'Multi-language Arabic support',
      ],
    },
    'thailand-sea': {
      region: 'Thailand & Southeast Asia',
      items: [
        'TM30 immigration reporting',
        '7% VAT automation',
        'Thai hotel tax compliance',
        'Foreign guest registration',
      ],
    },
    australia: {
      region: 'Australia & Oceania',
      items: [
        '10% GST reporting & BAS',
        'State tourism levies',
        'Privacy Act compliance',
        'ACCC pricing guidelines',
      ],
    },
    japan: {
      region: 'Japan',
      items: [
        'City-specific accommodation tax',
        'Ryokan licensing support',
        'Foreign guest registry (\u6CD5\u5B9A\u5E33\u7C3F)',
        'JCT invoice compliance',
      ],
    },
    india: {
      region: 'India',
      items: [
        'GST 12%/18% slab automation',
        'C-Form & FRRO reporting',
        'FSSAI food safety compliance',
        'DPDP data protection',
      ],
    },
  },
  ctaTitle: 'Need compliance for another region?',
  ctaSubtitle:
    'HotelStack supports custom compliance frameworks. Contact our team to discuss region-specific tax, legal, and data protection requirements.',
};

const ar: ComplianceHubContent = {
  heroBadge: '٦ مناطق مغطاة',
  heroTitle: 'الامتثال الإقليمي',
  heroSubtitle:
    'حساب الضرائب تلقائياً، وتسجيل الضيوف، وحماية البيانات عبر ٦ مناطق عالمية. حافظ على الامتثال بسهولة مع أطر تنظيمية مدمجة مصممة لكل ولاية قضائية.',
  regions: {
    'eu-uk': {
      region: 'الاتحاد الأوروبي والمملكة المتحدة',
      items: [
        'حماية بيانات GDPR وحق المحو',
        'أتمتة ضريبة القيمة المضافة المتعددة (٠-٢٧%)',
        'امتثال مدفوعات PSD٢/SCA',
        'إدارة موافقة ملفات تعريف الارتباط ePrivacy',
      ],
    },
    'united-states': {
      region: 'الولايات المتحدة',
      items: [
        'حساب الضرائب حسب كل ولاية',
        'التقييم الذاتي لـ PCI DSS',
        'تقارير امتثال ADA',
        'أتمتة ضريبة الإشغال المحلية',
      ],
    },
    'uae-middle-east': {
      region: 'الإمارات والشرق الأوسط',
      items: [
        'تتبع رسوم الدرهم السياحي',
        'فواتير ضريبة القيمة المضافة ٥% مع TRN',
        'أتمتة الرسوم البلدية',
        'دعم متعدد اللغات بالعربية',
      ],
    },
    'thailand-sea': {
      region: 'تايلاند وجنوب شرق آسيا',
      items: [
        'تقارير الهجرة TM٣٠',
        'أتمتة ضريبة القيمة المضافة ٧%',
        'امتثال ضريبة الفنادق التايلاندية',
        'تسجيل الضيوف الأجانب',
      ],
    },
    australia: {
      region: 'أستراليا وأوقيانوسيا',
      items: [
        'تقارير GST ١٠% و BAS',
        'رسوم السياحة الحكومية',
        'امتثال قانون الخصوصية',
        'إرشادات تسعير ACCC',
      ],
    },
    japan: {
      region: 'اليابان',
      items: [
        'ضريبة الإقامة حسب المدينة',
        'دعم ترخيص الريوكان',
        'سجل الضيوف الأجانب (\u6CD5\u5B9A\u5E33\u7C3F)',
        'امتثال فواتير JCT',
      ],
    },
    india: {
      region: 'الهند',
      items: [
        'أتمتة شرائح GST ١٢%/١٨%',
        'تقارير C-Form و FRRO',
        'امتثال سلامة الغذاء FSSAI',
        'حماية بيانات DPDP',
      ],
    },
  },
  ctaTitle: 'هل تحتاج امتثالاً لمنطقة أخرى؟',
  ctaSubtitle:
    'يدعم HotelStack أطر الامتثال المخصصة. تواصل مع فريقنا لمناقشة متطلبات الضرائب والقانون وحماية البيانات الخاصة بمنطقتك.',
};

const th: ComplianceHubContent = {
  heroBadge: 'ครอบคลุม 6 ภูมิภาค',
  heroTitle: 'การปฏิบัติตามกฎระเบียบระดับภูมิภาค',
  heroSubtitle:
    'การคำนวณภาษีอัตโนมัติ การลงทะเบียนผู้เข้าพัก และการคุ้มครองข้อมูลใน 6 ภูมิภาคทั่วโลก ปฏิบัติตามกฎระเบียบได้อย่างง่ายดายด้วยกรอบการกำกับดูแลในตัวที่ปรับให้เหมาะกับแต่ละเขตอำนาจศาล',
  regions: {
    'eu-uk': {
      region: 'สหภาพยุโรปและสหราชอาณาจักร',
      items: [
        'การคุ้มครองข้อมูล GDPR และสิทธิ์ในการลบ',
        'ระบบอัตโนมัติ VAT หลายอัตรา (0-27%)',
        'การปฏิบัติตามการชำระเงิน PSD2/SCA',
        'การจัดการความยินยอมคุกกี้ ePrivacy',
      ],
    },
    'united-states': {
      region: 'สหรัฐอเมริกา',
      items: [
        'การคำนวณภาษีรายรัฐ',
        'การประเมินตนเอง PCI DSS',
        'รายงานการปฏิบัติตาม ADA',
        'ระบบอัตโนมัติภาษีการเข้าพักท้องถิ่น',
      ],
    },
    'uae-middle-east': {
      region: 'สหรัฐอาหรับเอมิเรตส์และตะวันออกกลาง',
      items: [
        'การติดตามค่าธรรมเนียม Tourism Dirham',
        'ออกใบแจ้งหนี้ VAT 5% พร้อม TRN',
        'ระบบอัตโนมัติค่าธรรมเนียมเทศบาล',
        'รองรับหลายภาษารวมถึงภาษาอาหรับ',
      ],
    },
    'thailand-sea': {
      region: 'ไทยและเอเชียตะวันออกเฉียงใต้',
      items: [
        'รายงานตรวจคนเข้าเมือง TM30',
        'ระบบอัตโนมัติ VAT 7%',
        'การปฏิบัติตามภาษีโรงแรมไทย',
        'การลงทะเบียนผู้เข้าพักต่างชาติ',
      ],
    },
    australia: {
      region: 'ออสเตรเลียและโอเชียเนีย',
      items: [
        'รายงาน GST 10% และ BAS',
        'ค่าธรรมเนียมการท่องเที่ยวของรัฐ',
        'การปฏิบัติตาม Privacy Act',
        'แนวปฏิบัติด้านราคา ACCC',
      ],
    },
    japan: {
      region: 'ญี่ปุ่น',
      items: [
        'ภาษีที่พักเฉพาะเมือง',
        'สนับสนุนใบอนุญาตเรียวกัง',
        'ทะเบียนผู้เข้าพักต่างชาติ (\u6CD5\u5B9A\u5E33\u7C3F)',
        'การปฏิบัติตามใบแจ้งหนี้ JCT',
      ],
    },
    india: {
      region: 'อินเดีย',
      items: [
        'ระบบอัตโนมัติอัตรา GST 12%/18%',
        'รายงาน C-Form และ FRRO',
        'การปฏิบัติตามความปลอดภัยอาหาร FSSAI',
        'การคุ้มครองข้อมูล DPDP',
      ],
    },
  },
  ctaTitle: 'ต้องการการปฏิบัติตามกฎระเบียบสำหรับภูมิภาคอื่นหรือไม่?',
  ctaSubtitle:
    'HotelStack รองรับกรอบการปฏิบัติตามกฎระเบียบแบบกำหนดเอง ติดต่อทีมของเราเพื่อหารือเกี่ยวกับข้อกำหนดด้านภาษี กฎหมาย และการคุ้มครองข้อมูลเฉพาะภูมิภาค',
};

const es: ComplianceHubContent = {
  heroBadge: '6 Regiones Cubiertas',
  heroTitle: 'Cumplimiento Regional',
  heroSubtitle:
    'Cálculo automático de impuestos, registro de huéspedes y protección de datos en 6 regiones globales. Mantenga el cumplimiento sin esfuerzo con marcos regulatorios integrados adaptados a cada jurisdicción.',
  regions: {
    'eu-uk': {
      region: 'UE y Reino Unido',
      items: [
        'Protección de datos GDPR y derecho al olvido',
        'Automatización de IVA multitasa (0-27%)',
        'Cumplimiento de pagos PSD2/SCA',
        'Gestión de consentimiento de cookies ePrivacy',
      ],
    },
    'united-states': {
      region: 'Estados Unidos',
      items: [
        'Cálculo de impuestos estado por estado',
        'Autoevaluación PCI DSS',
        'Informes de cumplimiento ADA',
        'Automatización del impuesto de ocupación local',
      ],
    },
    'uae-middle-east': {
      region: 'EAU y Oriente Medio',
      items: [
        'Seguimiento de la tasa Tourism Dirham',
        'Facturación IVA 5% con TRN',
        'Automatización de tasas municipales',
        'Soporte multilingue en árabe',
      ],
    },
    'thailand-sea': {
      region: 'Tailandia y Sudeste Asiático',
      items: [
        'Informes de inmigración TM30',
        'Automatización de IVA 7%',
        'Cumplimiento fiscal hotelero tailandés',
        'Registro de huéspedes extranjeros',
      ],
    },
    australia: {
      region: 'Australia y Oceanía',
      items: [
        'Informes GST 10% y BAS',
        'Tasas turísticas estatales',
        'Cumplimiento de Privacy Act',
        'Directrices de precios ACCC',
      ],
    },
    japan: {
      region: 'Japón',
      items: [
        'Impuesto de alojamiento por ciudad',
        'Soporte de licencias Ryokan',
        'Registro de huéspedes extranjeros (\u6CD5\u5B9A\u5E33\u7C3F)',
        'Cumplimiento de facturas JCT',
      ],
    },
    india: {
      region: 'India',
      items: [
        'Automatización de tramos GST 12%/18%',
        'Informes C-Form y FRRO',
        'Cumplimiento de seguridad alimentaria FSSAI',
        'Protección de datos DPDP',
      ],
    },
  },
  ctaTitle: '¿Necesita cumplimiento para otra región?',
  ctaSubtitle:
    'HotelStack admite marcos de cumplimiento personalizados. Contacte a nuestro equipo para analizar los requisitos fiscales, legales y de protección de datos específicos de su región.',
};

const hi: ComplianceHubContent = {
  heroBadge: '6 क्षेत्र कवर',
  heroTitle: 'क्षेत्रीय अनुपालन',
  heroSubtitle:
    '6 वैश्विक क्षेत्रों में स्वचालित कर गणना, अतिथि पंजीकरण और डेटा सुरक्षा। प्रत्येक क्षेत्राधिकार के लिए अनुकूलित अंतर्निहित नियामक ढांचे के साथ सहज अनुपालन बनाए रखें।',
  regions: {
    'eu-uk': {
      region: 'यूरोपीय संघ और यूनाइटेड किंगडम',
      items: [
        'GDPR डेटा सुरक्षा और मिटाने का अधिकार',
        'मल्टी-रेट VAT स्वचालन (0-27%)',
        'PSD2/SCA भुगतान अनुपालन',
        'ePrivacy कुकी सहमति प्रबंधन',
      ],
    },
    'united-states': {
      region: 'संयुक्त राज्य अमेरिका',
      items: [
        'राज्य-दर-राज्य कर गणना',
        'PCI DSS स्व-मूल्यांकन',
        'ADA अनुपालन रिपोर्टिंग',
        'स्थानीय अधिभोग कर स्वचालन',
      ],
    },
    'uae-middle-east': {
      region: 'संयुक्त अरब अमीरात और मध्य पूर्व',
      items: [
        'Tourism Dirham शुल्क ट्रैकिंग',
        'TRN के साथ 5% VAT इनवॉइसिंग',
        'नगरपालिका शुल्क स्वचालन',
        'बहुभाषी अरबी समर्थन',
      ],
    },
    'thailand-sea': {
      region: 'थाईलैंड और दक्षिण पूर्व एशिया',
      items: [
        'TM30 आप्रवासन रिपोर्टिंग',
        '7% VAT स्वचालन',
        'थाई होटल कर अनुपालन',
        'विदेशी अतिथि पंजीकरण',
      ],
    },
    australia: {
      region: 'ऑस्ट्रेलिया और ओशिनिया',
      items: [
        '10% GST रिपोर्टिंग और BAS',
        'राज्य पर्यटन शुल्क',
        'Privacy Act अनुपालन',
        'ACCC मूल्य निर्धारण दिशानिर्देश',
      ],
    },
    japan: {
      region: 'जापान',
      items: [
        'शहर-विशिष्ट आवास कर',
        'रियोकान लाइसेंसिंग समर्थन',
        'विदेशी अतिथि रजिस्ट्री (法定帳簿)',
        'JCT इनवॉइस अनुपालन',
      ],
    },
    india: {
      region: 'भारत',
      items: [
        'GST 12%/18% स्लैब स्वचालन',
        'C-Form और FRRO रिपोर्टिंग',
        'FSSAI खाद्य सुरक्षा अनुपालन',
        'DPDP डेटा सुरक्षा',
      ],
    },
  },
  ctaTitle: 'किसी अन्य क्षेत्र के लिए अनुपालन चाहिए?',
  ctaSubtitle:
    'HotelStack कस्टम अनुपालन फ्रेमवर्क का समर्थन करता है। अपने क्षेत्र-विशिष्ट कर, कानूनी और डेटा सुरक्षा आवश्यकताओं पर चर्चा करने के लिए हमारी टीम से संपर्क करें।',
};

export const COMPLIANCE_HUB_CONTENT: LocaleContentMap<ComplianceHubContent> = {
  en, ar, th, es, fr, de, ja, zh, hi,
};
